import { AdsGroupAd } from '@betterfunnel/types';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import * as Sentry from '@sentry/react';
import {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { InView } from 'react-intersection-observer';
import { useLocation, useSearchParams } from 'react-router-dom';
import Loader from '../components/Common/Loader';
import MenuButton, { MenuOption } from '../components/Common/MenuButton';
import TextField from '../components/Common/TextField';
import ToggleButton from '../components/Common/ToggleButton';
import ConnectFacebookAds from '../components/ConnectFacebookAds';
import { AdsAccountContext } from '../contexts/AdsAccountContext';
import { AuthContext } from '../contexts/AuthContext';
import { UserContext } from '../contexts/UserContext';
import ConnectAdsAccountsModal from '../modals/ConnectAdsAccountsModal';
import CreateDynamicPageModal from '../modals/CreateDynamicPageModal';
import { WithId, classNames, groupBy } from '../utils';
import { getFacebookUrl } from '../utils/auth';

const scopes = ['ads_read', 'public_profile', 'email', 'business_management'];

export default function FacebookAds() {
  const { selectedAccount } = useContext(UserContext);

  if (!selectedAccount) return <></>;

  return selectedAccount.data().facebookAdsAccountConnected ? (
    <ConnectedToAdsAccount />
  ) : (
    <NotConnectedToAdsAccount />
  );
}

function NotConnectedToAdsAccount() {
  return (
    <div className="flex h-full flex-col items-center justify-center">
      <ConnectFacebookAds />
    </div>
  );
}

type GroupedAds = {
  group: string;
  ads: WithId<AdsGroupAd>[];
}[];

function ConnectedToAdsAccount() {
  const {
    loadingAds,
    ads,
    loadMoreAds,
    query,
    setQuery,
    filter,
    setFilter,
    resyncAdsAccounts,
    deleteAdsAccounts,
  } = useContext(AdsAccountContext);
  const [filtersChanged, setFiltersChanged] = useState(false);

  const groupedAds: GroupedAds | undefined = useMemo(() => {
    if (!ads) return undefined;

    const grouped = groupBy(ads, (ad) => ad.adGroupId);

    return Object.entries(grouped).map(([, v]) => ({
      group: v[0].adGroupName,
      ads: v,
    }));
  }, [ads]);

  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { firebaseUser } = useContext(AuthContext);
  const { createOAuthRequest } = useContext(UserContext);

  const [connectAccountsModalOpen, setConnectAccountsModalOpen] =
    useState<string>();
  const [createDynamicPageModalOpen, setCreateDynamicPageModalOpen] =
    useState(false);
  const [selectedAdAccountAd, setSelectedAdAccountAd] = useState<
    AdsGroupAd & { id: string }
  >();

  const handleResyncClick = useCallback(() => {
    resyncAdsAccounts().catch((e) => {
      console.error(e);
      Sentry.captureException(e);
    });
  }, [resyncAdsAccounts]);

  const handleDisconnectClick = useCallback(() => {
    deleteAdsAccounts('facebook').catch((e) => {
      console.error(e);
      Sentry.captureException(e);
    });
  }, [deleteAdsAccounts]);

  const handleConnectAdsAccountClick = useCallback(async () => {
    try {
      const from = location.pathname;
      const state = await createOAuthRequest('facebook', from, scopes);

      window.location.replace(getFacebookUrl(from, state, scopes));
    } catch (e) {
      Sentry.captureException(e);
      console.error(e);
    }
  }, [createOAuthRequest, location.pathname, firebaseUser]);

  const menuItems: MenuOption[] = useMemo(
    () => [
      { title: 'Re-sync', onClick: handleResyncClick },
      { title: 'Connect another', onClick: handleConnectAdsAccountClick },
      { title: 'Disconnect', onClick: handleDisconnectClick },
    ],
    [handleResyncClick],
  );

  // const getLPCampaign = useCallback(
  //   (dynamicPageId: string) => {
  //     return dynamicPages?.find((c) => c.id === dynamicPageId);
  //   },
  //   [dynamicPages],
  // );

  // const handleGenerateClick = useCallback((ad: AdsGroupAd & { id: string }) => {
  //   const adId = ad.id;

  //   track('create_campaign_from_ad_button_click', {
  //     adId,
  //     provider: ad.provider,
  //   });

  //   if (loadingRows[adId]) return;

  //   setSelectedAdAccountAd(ad);
  //   setCreateDynamicPageModalOpen(true);
  // }, []);

  useEffect(() => {
    const facebookAdsRequestId = searchParams.get('facebookAdsRequestId');
    if (!facebookAdsRequestId) return;

    setConnectAccountsModalOpen(facebookAdsRequestId);
  }, [searchParams]);

  return (
    <>
      <ConnectAdsAccountsModal
        requestId={connectAccountsModalOpen || ''}
        provider="facebook"
        open={!!connectAccountsModalOpen}
        onClose={() => {
          setSearchParams(new URLSearchParams());
          setConnectAccountsModalOpen(undefined);
        }}
      />
      <CreateDynamicPageModal
        adAccountsAd={selectedAdAccountAd}
        open={createDynamicPageModalOpen}
        onClose={() => {
          setSelectedAdAccountAd(undefined);
          setCreateDynamicPageModalOpen(false);
        }}
      />
      <div className="h-full pt-4 md:px-12 md:pt-16">
        <div className="flex h-full flex-col px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-6">
            <div className="sm:flex-auto">
              <h1 className="text-base font-semibold leading-6 text-gray-900">
                Facebook Ads
              </h1>
              <p className="mt-2 text-sm text-gray-700">
                Ads from your facebook account.
              </p>
            </div>
            <ToggleButton<'all' | 'enabled'>
              value={filter?.status !== 'enabled' ? 'all' : 'enabled'}
              items={[
                { value: 'all', title: 'All' },
                { value: 'enabled', title: 'Enabled' },
              ]}
              onChange={(selected) => {
                setFilter({
                  ...filter,
                  status: selected === 'enabled' ? selected : undefined,
                });
                setFiltersChanged(true);
              }}
            />
            <TextField
              value={query}
              startIcon={<MagnifyingGlassIcon className="h-4 w-4" />}
              onChange={(e) => {
                setQuery(e.target.value);
                setFiltersChanged(true);
              }}
              placeholder="Search..."
            />
            <MenuButton items={menuItems} />
          </div>
          <div className="mt-8 flow-root flex-1 overflow-scroll">
            <div className="-mx-4 -my-2 sm:-mx-6 lg:-mx-8">
              <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                <table className="min-w-full">
                  <thead className="sticky top-0 z-10 bg-white">
                    <tr>
                      <th
                        scope="col"
                        className="py-3.5 pr-3 ps-5 text-left text-sm font-semibold text-gray-900"
                      >
                        Ad
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        Ad Group
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        Ad Group Id
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        Campaign
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        Campaign Id
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        Status
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        Ad Type
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        Clicks
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        CTR
                      </th>
                      <th
                        scope="col"
                        className="bg-white py-3.5 pl-3 pr-4 sm:sticky sm:right-0 sm:pr-0"
                      >
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white">
                    {groupedAds?.map((adGroup) => (
                      <Fragment key={adGroup.group}>
                        <tr className="border-t border-gray-200">
                          <th
                            colSpan={11}
                            scope="colgroup"
                            className="bg-gray-50 py-2 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-3"
                          >
                            {adGroup.group}
                          </th>
                        </tr>
                        {adGroup.ads.map((ad, adIdx) => {
                          const adData = ad;
                          return (
                            <tr
                              key={ad.id}
                              className={classNames(
                                adIdx === 0
                                  ? 'border-gray-300'
                                  : 'border-gray-200',
                                'border-t',
                              )}
                            >
                              <td className="flex w-[400px] flex-col gap-1 py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-3">
                                <span>
                                  {adData?.headlines?.[0]?.text ?? ''}
                                </span>
                                <span className="text-gray-500">
                                  {adData?.descriptions?.[0]?.text ?? ''}
                                </span>
                              </td>
                              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                {adData.adGroupName}
                              </td>
                              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                {adData.adGroupId}
                              </td>
                              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                {adData.campaignName}
                              </td>
                              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                {adData.campaignId}
                              </td>
                              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                {adData.status}
                              </td>
                              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                {adData.type}
                              </td>
                              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                {adData.metrics?.clicks}
                              </td>
                              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                {adData.metrics?.ctr
                                  ? (adData.metrics?.ctr ?? 0).toFixed(2)
                                  : ''}
                              </td>
                              <td className="whitespace-nowrap bg-white py-4 pl-3 pr-4 text-right text-sm font-medium sm:sticky sm:right-0 sm:pr-3">
                                <div className="flex justify-end gap-2">
                                  {/* {!adData.lpCampaignId &&
                                  !loadingRows[ad.id] ? (
                                    <Button
                                      onClick={() =>
                                        handleGenerateClick({
                                          ...ad,
                                        })
                                      }
                                    >
                                      Create Dynamic Page
                                    </Button>
                                  ) : getLPCampaign(
                                      adData.lpCampaignId ?? '',
                                    ) ? (
                                    <>
                                      <Button
                                        onClick={() =>
                                          navigate(
                                            `../pages/${adData.lpCampaignId}/details`,
                                          )
                                        }
                                      >
                                        Manage
                                      </Button>
                                      <Button
                                        onClick={() =>
                                          window.open(
                                            `https://${
                                              primaryDomain?.data().domain
                                            }/${adData.lpCampaignPath}`,
                                          )
                                        }
                                      >
                                        Visit
                                      </Button>
                                    </>
                                  ) : (
                                    <div className="flex w-36 justify-center">
                                      <Loader size={4} />
                                    </div>
                                  )} */}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </Fragment>
                    ))}
                    {!groupedAds?.length && (!filtersChanged || loadingAds) ? (
                      [0, 1, 2, 3, 4].map((v, index) => (
                        <tr
                          key={v}
                          className={classNames(
                            index === 0 ? 'border-gray-300' : 'border-gray-200',
                            'animate-pulse border-t',
                          )}
                        >
                          <td className="flex flex-col gap-1 whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-3">
                            <span className="mb-2 h-2.5 w-24 rounded-full bg-gray-300"></span>
                            <span className="h-2 w-32 rounded-full bg-gray-200"></span>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm">
                            <div className="h-2 w-16 rounded-full bg-gray-200"></div>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm">
                            <div className="h-2 w-24 rounded-full bg-gray-200"></div>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm">
                            <div className="h-2 w-16 rounded-full bg-gray-200"></div>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm">
                            <div className="h-2 w-16 rounded-full bg-gray-200"></div>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm">
                            <div className="h-2 w-16 rounded-full bg-gray-200"></div>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm">
                            <div className="h-2 w-32 rounded-full bg-gray-200"></div>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm"></td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm"></td>
                          <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-3"></td>
                        </tr>
                      ))
                    ) : (
                      <></>
                    )}
                  </tbody>
                </table>
              </div>
              {groupedAds?.length &&
              groupedAds.reduce((p, c) => p + c.ads.length, 0) % 100 === 0 ? (
                <InView
                  as="div"
                  className="flex w-full justify-center p-9"
                  onChange={(inView) => {
                    if (!inView) return;
                    loadMoreAds();
                  }}
                >
                  <Loader />
                </InView>
              ) : (
                <></>
              )}
              {!groupedAds?.length && filtersChanged && !loadingAds ? (
                <p className="mt-12 w-full text-center text-gray-500">
                  Nothing found
                </p>
              ) : (
                <></>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
